import { UseMutationOptions, useMutation, useQuery } from "@tanstack/react-query";
import { MAPBOX_TOKEN } from "../config/constants";
import { MappingSourceType } from "../config/enums/MappingSourceType.enum";
import { successMessage } from "../helpers/ErrorHandler";
import { ApiServiceErr, axiosApi, axiosMapBox, axiosMapBoxV5 } from "./apiService";

interface drawingTool {
  _id: string;
  tenantId: string;
  salesUnitId: string;
  lockStatus?: boolean;
  chartNodes: [
    {
      id: string;
      type: string;
      data: any;
      position: any;
      width: number;
      height: number;
    }
  ];
  chartEdges: [
    {
      id: string;
      type: string;
      data: any;
      position: any;
      width: number;
      height: number;
      style: any;
      markerEnd: any;
      source: string;
      sourceHandle: string;
      target: string;
    }
  ];
}

interface ProductSalesUnits {
  productName: string;
  productId: string;
  salesUnitName: string;
  salesUnitId: string;
  drawingId?: string;
}

interface ProductSalesUnitInputs {
  mappingType: MappingSourceType;
  searchKey?: string;
}

export const useGetDrawingToolData = (
  mappingType: MappingSourceType,
  salesUnitId: string | undefined,
  ownerId: string | undefined
) =>
  useQuery<drawingTool, ApiServiceErr>(
    [salesUnitId, mappingType, ownerId, "getDrawingToolData"],
    async () => {
      const response = await axiosApi.get(
        `/drawing-tool/${mappingType}/${salesUnitId}/${ownerId}`
      );
      return response?.data || null;
    },
    {
      cacheTime: 0,
      enabled: !!salesUnitId && !!ownerId
    }
  );

export const useCreateDrawing = (mappingType: MappingSourceType, opt?: UseMutationOptions) =>
  useMutation<any, ApiServiceErr, any>(async data => {
    const response = await axiosApi.post(`/drawing-tool/${mappingType}`, data);
    // SHARED5 Fix: Remove toast - let calling component handle it
    return response.data;
  }, opt);

export const useUpdateDrawing = (mappingType: MappingSourceType, opt?: UseMutationOptions) =>
  useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.put(`/drawing-tool/${mappingType}/${data._id}`, data);
    // SHARED5 Fix: Remove toast - let calling component handle it to avoid duplicates
    return response.data;
  }, opt);

export const usePutDrawingToolDataToQueue = (
  mappingType: MappingSourceType,
  opt?: UseMutationOptions
) =>
  useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.put(`/drawing-tool/${mappingType}/queue/${data._id}`);
    // Toast removed - let calling component handle it
    return response.data;
  }, opt);

export const useGetProductSalesUnitsNotSavedDrawing = ({
  mappingType,
  searchKey
}: ProductSalesUnitInputs) =>
  useQuery<ProductSalesUnits[], ApiServiceErr>(
    [searchKey, mappingType, "getProductSalesUnitsNotSavedDrawing"],
    async () => {
      const response = await axiosApi.get(
        `/drawing-tool/${mappingType}/sales-units-except-drawing`,
        {
          params: { searchKey }
        }
      );
      return response.data;
    }
  );

export const useGetProductSalesUnitsSavedDrawing = ({
  mappingType,
  searchKey
}: ProductSalesUnitInputs) =>
  useQuery<ProductSalesUnits[], ApiServiceErr>(
    [searchKey, mappingType, "getProductSalesUnitsSavedDrawing"],
    async () => {
      const response = await axiosApi.get(`/drawing-tool/${mappingType}/sales-units-drawing`, {
        params: { searchKey }
      });
      return response.data;
    }
  );

export const useCopyDrawingToolData = (mappingType: MappingSourceType, opt?: UseMutationOptions) =>
  useMutation<string, ApiServiceErr, any>(async ({ drawingId, ownerId, sourceId }) => {
    const response = await axiosApi.post(`/drawing-tool/${mappingType}/copy/${drawingId}`, null, {
      params: { ownerId, sourceId }
    });
    successMessage(response.data);
    return response.data;
  }, opt);

export const useSendErrorDetails = (
  mappingType: MappingSourceType,
  opt?: UseMutationOptions<
    { message: string },
    ApiServiceErr,
    { ownerId: string; sourceId: string }
  >
) =>
  useMutation<
    { message: string },
    ApiServiceErr,
    {
      ownerId: string;
      sourceId: string;
      companyName: string;
      mappingUrl: string;
    }
  >(
    async ({ ownerId, sourceId, companyName, mappingUrl }) => {
      const response = await axiosApi.post(
        `/drawing-tool/${mappingType}/send-error-details`,
        { ownerId, sourceId, companyName, mappingUrl }
      );
      return response.data;
    },
    {
      onSuccess: data => {
        successMessage(data.message);
      },
      ...opt
    }
  );

export const useUploadMappingImageAction = (
  mappingType: MappingSourceType,
  opt?: UseMutationOptions
) =>
  useMutation<string, ApiServiceErr, any>(async data => {
    const response = await axiosApi.post(`/drawing-tool/${mappingType}/upload-final-image`, data, {
      headers: {
        ContentType: "multipart/form-data"
      }
    });
    successMessage(response.data);
    return response.data;
  }, opt);

export const useGetMappingImageUri = (mappingType: MappingSourceType, imageKey: string) =>
  useQuery<{ signedUrl: string }, ApiServiceErr>(
    [imageKey, "getMappingImageUri"],
    async () => {
      const response = await axiosApi.get(`/drawing-tool/${mappingType}/image-uri`, {
        params: { imageKey }
      });
      return response.data;
    },
    {
      enabled: !!imageKey
    }
  );

export const useDeleteMappingData = (mappingType: MappingSourceType, opt?: UseMutationOptions) =>
  useMutation<string, ApiServiceErr, any>(async drawingId => {
    const response = await axiosApi.delete(`/drawing-tool/${mappingType}/settled/${drawingId}`);
    successMessage(response.data);
    return response.data;
  }, opt);

export const useGetRegionLocation = (regionName: string, limit: number = 100) =>
  useQuery<any, ApiServiceErr>(
    [regionName, "useGetRegionLocation"],
    async () => {
      const response = await axiosMapBox.get(`/forward`, {
        params: { q: regionName, limit: limit, access_token: MAPBOX_TOKEN }
      });
      return response.data;
    },
    {
      enabled: !!regionName
    }
  );

export const useGetCountryAndRegionData = (locationData: any) =>
  useQuery<any, ApiServiceErr>(
    [locationData, "useGetCountryAndRegionData"],
    async () => {
      const response = await axiosMapBoxV5.get(
        `/mapbox.places/${locationData.lng},${locationData.lat}.json?access_token=${MAPBOX_TOKEN}`
      );
      const countryRegionValues = response?.data?.features.reduce(
        (acc: any, feature: any) => {
          if (feature.place_type.includes("country")) {
            acc.country = feature.properties.short_code.toUpperCase();
          } else if (feature.place_type.includes("region")) {
            const regionCode = feature.properties.short_code?.split("-")[1];
            acc.region = regionCode;
          }
          return acc;
        },
        {
          country: "",
          region: ""
        }
      );
      return countryRegionValues;
    },
    {
      enabled: !!locationData
    }
  );
